import {useEffect, useState} from "react";

/**
 * Tracks whether an element's content is wider than its visible box, keeping the
 * answer up to date as the element (e.g. on window resize) or its content
 * changes size.
 *
 * Returns the current state and a ref callback for the element to measure.
 */
export const useOverflowsHorizontally = <T extends HTMLElement>(): [
    boolean,
    (element: T | null) => void,
] => {
    const [element, setElement] = useState<T | null>(null);
    const [overflows, setOverflows] = useState(false);

    useEffect(() => {
        if (!element) return;

        const update = () =>
            setOverflows(element.scrollWidth > element.clientWidth);

        update();

        const observer = new ResizeObserver(update);
        observer.observe(element);
        // The content can change width without the container resizing, e.g. a
        // table growing as content streams in.
        if (element.firstElementChild) {
            observer.observe(element.firstElementChild);
        }

        return () => observer.disconnect();
    }, [element]);

    return [overflows, setElement];
};
